import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { SettingsResponse, Site, SiteGroups } from '@cloudsign/shared';
import { z } from 'zod';
import { api, ApiClientError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, EmptyState, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { timeZoneNames } from '../sites/api';
import { useActiveGroups } from './api';

const RemoteList = z.array(Site);
const listKey = ['remote-site', 'list'];

function CreateForm({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(t('siteF.remote.defaultName'));
  const [timeZone, setTimeZone] = useState('Europe/London');
  const create = useMutation({
    mutationFn: () => api.post('/remote-site', { body: { name, timeZone }, schema: Site }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: listKey });
      onDone();
    },
  });
  const errs = fieldErrors(create.error);
  return (
    <Card title={t('siteF.remote.new')} description={t('siteF.remote.newHint')} className="mb-6">
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate();
        }}
      >
        <div className="sm:col-span-2">
          <ErrorLine error={create.error} />
        </div>
        <TextField label={t('siteF.remote.name')} required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} error={errs['name']} />
        <SelectField label={t('siteF.remote.timeZone')} value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
          {timeZoneNames(timeZone).map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </SelectField>
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={create.isPending || name.trim() === ''}>
            {t('siteF.remote.create')}
          </Button>
          <Button onClick={onDone}>{t('common.cancel')}</Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * Which groups may sign in at a remote site. A new remote site shows no groups at all, so nobody can sign in until the admin picks
 * some here. Saving sets the site's visible groups (PUT /sites/:id/groups) and, where the person may manage groups, each group's
 * "allowed to work remotely" setting, because the remote-site check refuses a group that is not allowed.
 */
function GroupsChooser({ site, canManageGroups }: { site: Site; canManageGroups: boolean }) {
  const qc = useQueryClient();
  const groups = useActiveGroups();
  const visible = useQuery({ queryKey: ['remote-site', 'groups', site.id], queryFn: () => api.get(`/sites/${site.id}/groups`, { schema: SiteGroups }) });
  const allowed = useQueries({
    queries: (groups.data ?? []).map((g) => ({
      queryKey: ['remote-site', 'allowed', g.id],
      queryFn: () => api.get(`/groups/${g.id}/settings/remote-site`, { schema: SettingsResponse }),
      enabled: canManageGroups,
    })),
  });
  const [picked, setPicked] = useState<string[] | null>(null);
  const save = useMutation({
    mutationFn: async (ids: string[]) => {
      await api.put(`/sites/${site.id}/groups`, { body: { mode: 'selected', groupIds: ids }, schema: SiteGroups });
      if (canManageGroups) {
        for (const [i, g] of (groups.data ?? []).entries()) {
          const was = allowed[i]?.data?.values['allowed'] === true;
          const now = ids.includes(g.id);
          if (was !== now) await api.patch(`/groups/${g.id}/settings/remote-site`, { body: { allowed: now }, schema: SettingsResponse });
        }
      }
    },
    onSuccess: async () => {
      setPicked(null);
      await qc.invalidateQueries({ queryKey: ['remote-site'] });
    },
  });

  if (groups.isPending || visible.isPending) return <Spinner />;
  if (groups.isError || visible.isError) return <ErrorPanel error={groups.error ?? visible.error} />;
  const current = visible.data.mode === 'all' ? groups.data.map((g) => g.id) : visible.data.groupIds;
  const chosen = picked ?? current;
  const toggle = (id: string) => setPicked(chosen.includes(id) ? chosen.filter((g) => g !== id) : [...chosen, id]);
  const noneChosen = current.length === 0;
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-slate-900">{t('siteF.remote.groups')}</h3>
      <p className="text-sm text-slate-600">{t('siteF.remote.groupsHint')}</p>
      {noneChosen && picked === null ? <Alert kind="info">{t('siteF.remote.noGroups')}</Alert> : null}
      <ErrorLine error={save.error} />
      <fieldset className="grid gap-2 sm:grid-cols-2">
        <legend className="sr-only">{t('siteF.remote.groups')}</legend>
        {groups.data.map((g) => (
          <CheckboxField key={g.id} label={g.name} disabled={save.isPending} checked={chosen.includes(g.id)} onChange={() => toggle(g.id)} />
        ))}
      </fieldset>
      <div className="flex gap-2">
        <Button variant="primary" disabled={picked === null || save.isPending} onClick={() => save.mutate(chosen)}>
          {t('siteF.remote.saveGroups')}
        </Button>
        <Button disabled={picked === null} onClick={() => setPicked(null)}>
          {t('settings.discard')}
        </Button>
      </div>
    </div>
  );
}

function RemoteSiteCard({ site, canManage, canDelete, canManageGroups }: { site: Site; canManage: boolean; canDelete: boolean; canManageGroups: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState(site.name);
  const [timeZone, setTimeZone] = useState(site.timeZone);
  const refresh = () => qc.invalidateQueries({ queryKey: listKey });
  const rename = useMutation({ mutationFn: () => api.patch(`/remote-site/${site.id}`, { body: { name, timeZone }, schema: Site }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api.delete(`/remote-site/${site.id}`), onSuccess: refresh });
  const archive = useMutation({ mutationFn: () => api.patch(`/sites/${site.id}`, { body: { status: 'archived' }, schema: Site }), onSuccess: refresh });
  const hasVisits = remove.error instanceof ApiClientError && remove.error.status === 409;
  const changed = name.trim() !== site.name || timeZone !== site.timeZone;
  return (
    <Card
      title={
        <span className="flex flex-wrap items-center gap-2">
          {site.name}
          <Badge tone="neutral">{t('siteF.remote.badge')}</Badge>
        </span>
      }
    >
      <div className="space-y-6">
        <form
          className="grid gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate();
          }}
        >
          <div className="sm:col-span-2">
            <ErrorLine error={rename.error} />
          </div>
          <TextField label={t('siteF.remote.name')} maxLength={80} disabled={!canManage} value={name} onChange={(e) => setName(e.target.value)} />
          <SelectField label={t('siteF.remote.timeZone')} disabled={!canManage} value={timeZone} onChange={(e) => setTimeZone(e.target.value)}>
            {timeZoneNames(timeZone).map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </SelectField>
          {canManage ? (
            <div className="sm:col-span-2">
              <Button type="submit" variant="primary" disabled={!changed || name.trim() === '' || rename.isPending}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </form>

        {canManage ? (
          <GroupsChooser site={site} canManageGroups={canManageGroups} />
        ) : null}

        {canDelete ? (
          <div className="space-y-2 border-t border-slate-200 pt-4">
            <ErrorLine error={hasVisits ? null : (remove.error ?? archive.error)} />
            {hasVisits ? (
              <Alert>
                <p>{t('error.remote_site_has_visits')}</p>
                {canManage ? (
                  <Button className="mt-2" onClick={() => archive.mutate()} disabled={archive.isPending}>
                    {t('siteF.remote.archive')}
                  </Button>
                ) : null}
              </Alert>
            ) : null}
            <ConfirmButton label={t('siteF.remote.delete')} pending={remove.isPending} onConfirm={() => remove.mutate()} />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/** Portal page "Remote site" (remote-site.md): create, rename and delete the working-remotely site, and choose who may sign in there. */
export function RemoteSitePage() {
  const canManage = useCan('sites.manage');
  const canDelete = useCan('sites.delete_remote');
  const canManageGroups = useCan('groups.manage');
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: listKey, queryFn: () => api.get('/remote-site', { schema: RemoteList }) });
  const sites = (q.data ?? []).filter((s) => s.status === 'active');
  return (
    <>
      <PageHeader
        title={t('siteF.remote.title')}
        description={t('siteF.remote.description')}
        actions={
          canManage ? (
            <Button variant="primary" disabled={creating} onClick={() => setCreating(true)}>
              {t('siteF.remote.add')}
            </Button>
          ) : null
        }
      />
      {creating ? <CreateForm onDone={() => setCreating(false)} /> : null}
      {q.isPending ? (
        <Spinner />
      ) : q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : sites.length === 0 ? (
        <EmptyState message={t('siteF.remote.empty')} />
      ) : (
        <div className="space-y-6">
          {sites.map((s) => (
            <RemoteSiteCard key={s.id} site={s} canManage={canManage} canDelete={canDelete} canManageGroups={canManageGroups} />
          ))}
        </div>
      )}
    </>
  );
}
